﻿export type FieldType =
  | "text"
  | "number"
  | "boolean"
  | "date"
  | "time"
  | "datetime"
  | "select"
  | "multi_select"
  | "reference";
export type WidgetKind =
  "list" | "detail" | "form" | "table" | "summary" | "metric";
export type ElementKind =
  "field" | "text" | "heading" | "divider" | "button" | "widget";
export type LayoutKind = "stack" | "tabs" | "dashboard" | "sidebar";
export type SlotKind = "main" | "tab" | "summary" | "aside";
export type Mode = "published" | "preview" | "print";
export interface FieldManifest {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  control: string;
  default?: unknown;
  config?: Record<string, unknown>;
  /** Present and true only for a hidden field (ADR 0198); absent means visible. */
  hidden?: boolean;
}
export interface CollectionManifest {
  id: string;
  label: string;
  primary_display_field_id: string;
  fields: FieldManifest[];
}
export type IconKind =
  "plus" | "check" | "calendar" | "clock" | "search" | "info" | "external_link";
export type OptionColor =
  "neutral" | "accent" | "success" | "warning" | "error" | "info";
export type ControlCommand =
  | {
      kind: "create_record";
      collection_id: string;
      defaults?: Record<string, unknown>;
    }
  | { kind: "open_view"; view_id: string }
  | { kind: "print"; print_form_id: string };
export interface ViewControl {
  id: string;
  label: string;
  icon?: IconKind;
  command: ControlCommand;
}
export type FilterOp =
  | "eq"
  | "neq"
  | "empty"
  | "not_empty"
  | "before_today"
  | "today"
  | "after_today";
export type MetricAggregate = "count" | "sum" | "avg" | "min" | "max";
export interface ViewElement {
  id: string;
  kind: ElementKind;
  span?: 1 | 2;
  config?: Record<string, unknown>;
}
export interface ViewSection {
  id: string;
  title?: string;
  description?: string;
  columns: 1 | 2;
  slot?: SlotKind;
  fab?: ViewControl;
  elements: ViewElement[];
}
export interface ViewManifest {
  id: string;
  label: string;
  role: "home" | "standard";
  show_in_navigation: boolean;
  context: { kind: "none" | "record"; collection_id?: string };
  layout?: LayoutKind;
  header_actions?: ViewControl[];
  fab?: ViewControl;
  sections: ViewSection[];
}
export interface CompiledManifest {
  manifest_version: "1.0";
  schema_format_version: "1.0";
  compiler_version: string;
  registry_version: string;
  checksum: string;
  collections: CollectionManifest[];
  views: ViewManifest[];
  actions: Record<string, unknown>[];
  print_forms: Record<string, unknown>[];
}
export interface AbilityRecord {
  id: string;
  collection_id: string;
  revision: number;
  payload: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}
export type Session = components["schemas"]["BootstrapSession"];
export interface Bootstrap {
  session: Session;
  abilities: {
    id: string;
    name: string;
    manifest: CompiledManifest;
    schema_version_id?: string | null;
    provider_key?: string | null;
    /** Records in the section; reminders count for the Reminders section. */
    record_count?: number;
    /** When a record last changed; absent while the section is empty. */
    updated_at?: string | null;
    /** Unpublished Draft of this section, summary only (AGE-016). */
    draft?: SectionDraft | null;
  }[];
  /** Whether the Mini App offers feedback controls on assistant replies. */
  feedback_enabled?: boolean;
}
export interface SectionDraft {
  revision: number;
  status: string;
  /** Cut from a version that is no longer active, or marked stale. */
  stale: boolean;
  updated_at: string;
}
export const WIDGET_KINDS: readonly WidgetKind[] = [
  "list",
  "detail",
  "form",
  "table",
  "summary",
  "metric",
];
export const ELEMENT_KINDS: readonly ElementKind[] = [
  "field",
  "text",
  "heading",
  "divider",
  "button",
  "widget",
];
export const ICONS: readonly IconKind[] = [
  "plus",
  "check",
  "calendar",
  "clock",
  "search",
  "info",
  "external_link",
];
export const MAX_HEADER_ACTIONS = 3;
export const MAX_LIST_FILTERS = 3;
export interface SlotRule {
  minSections: number;
  maxSections?: number;
  maxElements?: number;
  titleRequired?: boolean;
}
export interface LayoutRule {
  defaultSlot: SlotKind;
  slots: Partial<Record<SlotKind, SlotRule>>;
}
/** Mirrors `LAYOUTS` in the backend views compiler pass (ADR 0185). */
export const LAYOUTS: Record<LayoutKind, LayoutRule> = {
  stack: { defaultSlot: "main", slots: { main: { minSections: 0 } } },
  tabs: {
    defaultSlot: "tab",
    slots: { tab: { minSections: 2, maxSections: 6, titleRequired: true } },
  },
  dashboard: {
    defaultSlot: "main",
    slots: {
      summary: { minSections: 0, maxSections: 1, maxElements: 4 },
      main: { minSections: 0 },
    },
  },
  sidebar: {
    defaultSlot: "main",
    slots: {
      main: { minSections: 0 },
      aside: { minSections: 0, maxSections: 2 },
    },
  },
};
export const FIELD_CONTROLS: Record<FieldType, readonly string[]> = {
  text: ["text_input", "textarea"],
  number: ["number_input"],
  boolean: ["checkbox", "switch"],
  date: ["date_picker"],
  time: ["time_picker"],
  datetime: ["datetime_picker"],
  select: ["select"],
  multi_select: ["multi_select"],
  reference: ["reference_select"],
};
import type { components } from "./openapi";
